import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreHorizontal } from 'lucide-react';
import {
  Button,
  Input,
  IconButton,
  Skeleton,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { useNetworkSummary, type Network, type RouterNicInstance } from '@/api/opennebula';

/** Instances currently attached to a network. The API field is optional and may
 *  be an array of ids or a keyed object; absent means zero. */
export function attachedCount(n: Network): number {
  const att = (n as { attached?: unknown }).attached;
  if (Array.isArray(att)) return att.length;
  if (att && typeof att === 'object') return Object.keys(att).length;
  return 0;
}

const mutedCell: React.CSSProperties = { color: 'var(--vn-fg-muted)' };

// Leased/size + attached count live on the per-network detail, not the list — so each
// row fetches its own summary (mirrors @console). The three cells + the row actions
// share one query key per network, so React Query dedupes them into a single request.

/** Address leased: IPs in use over the subnet size (e.g. "3/254"). */
export function NetworkLeasedCell({ serviceId, networkId }: { serviceId: string; networkId: string }) {
  const { data, isLoading } = useNetworkSummary(serviceId, networkId);
  if (isLoading) return <Skeleton style={{ height: 14, width: 56 }} />;
  return <span style={mutedCell}>{data?.leased ?? 0}/{data?.size ?? '—'}</span>;
}

/** Number of instances attached to the network. */
export function NetworkAttachedCell({ serviceId, networkId }: { serviceId: string; networkId: string }) {
  const { data, isLoading } = useNetworkSummary(serviceId, networkId);
  if (isLoading) return <Skeleton style={{ height: 14, width: 24 }} />;
  return <span style={mutedCell}>{data?.attached?.length ?? 0}</span>;
}

/** VMs attached to a router NIC's network, as clickable chips that open the
 *  instance detail — mirrors @console's "Connected Instances" block. */
export function ConnectedVmsCell({ serviceId, instances }: { serviceId: string; instances?: RouterNicInstance[] }) {
  const navigate = useNavigate();
  if (!instances || instances.length === 0) return <span style={mutedCell}>—</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
      {instances.map((vm) => {
        const active = /active|running/i.test(vm.status ?? '');
        const title = [vm.ip, vm.status].filter(Boolean).join(' · ');
        return (
          <Badge
            key={vm.id}
            tone={active ? 'success' : 'neutral'}
            appearance="subtle"
            size="sm"
            dot
            title={title || undefined}
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/services/compute/${serviceId}/instances/${vm.id}`)}
          >
            {vm.name || vm.id}
          </Badge>
        );
      })}
    </div>
  );
}

/** Row actions for a network. Delete is blocked while instances are still attached
 *  (the backend rejects it) — the guard reads the per-row summary, not the list. */
export function NetworkRowActions({
  serviceId,
  network,
  onDelete,
}: {
  serviceId: string;
  network: Network;
  onDelete: (n: Network) => void;
}) {
  const { show } = useToast();
  const { data } = useNetworkSummary(serviceId, network.id);
  const attached = data?.attached?.length ?? 0;
  return (
    <RowMenu
      onDelete={() => {
        if (attached > 0) {
          show({ tone: 'danger', title: 'Detach all instances before deleting this network' });
          return;
        }
        onDelete(network);
      }}
    />
  );
}

export function RowMenu({ onDelete }: { onDelete: () => void }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton variant="ghost" size="sm" aria-label="Actions"><MoreHorizontal size={16} /></IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem tone="danger" onSelect={onDelete}>Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export interface CreateField {
  key: string;
  label: string;
  placeholder?: string;
}

export function SimpleCreateDialog({
  open,
  onOpenChange,
  title,
  fields,
  onCreate,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  fields: CreateField[];
  onCreate: { mutate: (payload: Record<string, unknown>) => void; isPending: boolean };
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const valid = fields.every((f) => (values[f.key] ?? '').trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {fields.map((f) => (
            <Field key={f.key} label={f.label}>
              {(p) => (
                <Input
                  {...p}
                  value={values[f.key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                />
              )}
            </Field>
          ))}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!valid || onCreate.isPending} onClick={() => onCreate.mutate(values)}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
